import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoMark } from '@/components/Logo';
import { toast } from 'sonner';

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 mb-12">
            <LogoMark size={40} />
            <span className="text-xl font-bold font-display tracking-tight">
              <span className="text-ink">Portfolio</span>
              <span className="text-signal-blue"> Pal</span>
            </span>
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-ink font-display tracking-tight">Create your account</h1>
            <p className="mt-2 text-muted-brand">Start managing your property portfolio today</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-brand">Full name</Label>
              <Input
                id="name"
                type="text"
                data-testid="signup-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="mt-2 h-11 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-brand">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="signup-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-11 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-brand">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="signup-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-11 pr-10 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-brand hover:text-ink transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-brand">Confirm password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                data-testid="signup-confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="mt-2 h-11 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              data-testid="signup-submit-btn"
              className="w-full h-11 bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl font-semibold"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Creating account...
                </span>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          {/* Login link */}
          <p className="mt-8 text-center text-muted-brand">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-signal-blue hover:text-anchor-blue transition-colors" data-testid="go-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-anchor-blue/70 z-10"></div>
        <img
          src="https://images.unsplash.com/photo-1687075430355-ed8df51c1670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGxhbmRsb3JkJTIwaG9sZGluZyUyMGtleXN8ZW58MHx8fHwxNzcxNzkwOTgxfDA&ixlib=rb-4.1.0&q=85"
          alt="Success"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            {/* Accent roofline */}
            <div className="w-16 h-1 bg-sunbeam-amber rounded-full mb-6"></div>
            <h2 className="text-3xl font-bold font-display tracking-tight">
              Join landlords who've simplified their workflow
            </h2>
            <p className="mt-4 text-lg text-white/80">
              Set up takes less than 5 minutes. Start adding properties right away.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
